import React, { FormEvent, useState } from 'react'
import {
    Container,
    Heading,
    SelectsContainer,
    Title,
    TitleContainer,
    SpinnerContainer,
    FormContainer
} from './PaymentInput.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons'
import TariffSelect from './components/TariffSelect.component'
import PolicyContextSelect from './components/PolicyContextSelect.component'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import { getFragmentData } from 'parkdepot-shared/gql'
import { Value } from 'baseui/select'
import EmptyCarpayOrderCard from '@components/CarpayOrderCard/EmptyCarpayOrderCard.component'
import Spinner from 'parkdepot-shared/components/Spinner'
import parseTariffs from '@utils/parseCarpayTariffs.utils'
import { B2COrderResponseFragment, LotType, SvLotType } from 'parkdepot-shared/gql/graphql'
import useLazyGetOrderPrice from '@hooks/useLazyGetOrderPrice'
import Voucher from '@components/Voucher/Voucher.component'
import useCarpayPaymentFlow from '@hooks/useCarpayPaymentFlow'
import { OrderPriceResponseFragment } from '@graphql/queries/getOrderPrice.query'
import Error from '@components/Error/Error.component'
import { useTranslation } from 'react-i18next'
import { carpayPaymentEmailRoute } from '../PaymentEmail/route.const'
import useGetOpenOrders from '@hooks/useGetOpenOrders'
import { useApolloClient } from '@apollo/client'
import FullScreenLoader from '@components/FullScreenLoader/FullScreenLoader.component'

const CarpayPaymentInput = () => {
    const client = useApolloClient()
    const [selectedOrder, setSelectedOrder] = useState<B2COrderResponseFragment | null>(null)
    const [lotType, setLotType] = useState<LotType | null>(null)
    const history = useHistory()
    const location = useLocation()
    const { t } = useTranslation()
    const { state, updateState } = useCarpayPaymentFlow()
    const { order_id } = useParams<{ order_id: string }>()
    const searchParams = new URLSearchParams(location.search)
    const plate = searchParams.get('plate')
    const [calculatePrice, { data: priceData, loading: loadingPrice }] = useLazyGetOrderPrice()

    const { data, loading, error } = useGetOpenOrders({
        variables: {
            plates: [plate!] // should exist because of skip
        },
        skip: !plate || !order_id,
        fetchPolicy: 'cache-and-network',
        pollInterval: 30000,
        onCompleted: (ordersData) => {
            const orders = ordersData.b2c_get_open_orders.orders.map((data) =>
                getFragmentData(OrderResponseFragment, data)
            )

            const selectedOrder = orders.find((order) => order.order.order_id === order_id)

            if (selectedOrder) {
                setSelectedOrder(selectedOrder)

                const lot = selectedOrder!.lot

                const lotType = lot.lot_type
                setLotType(lotType)

                if (lotType === 'PREPAID' && !state.parkingTariff) {
                    return
                }

                calculatePrice({
                    variables: {
                        order_id,
                        selected_parking_tariff: state.parkingTariff!,
                        parking_context_uuid: state.parkingContextUuid ?? undefined
                    }
                })
            }
        }
    })

    const handleTariffChange = (opt: Value) => {
        const tariff = opt[0].id!.toString()
        if (!tariff) return

        updateState({ parkingTariff: tariff })

        calculatePrice({
            variables: {
                order_id,
                selected_parking_tariff: tariff,
                parking_context_uuid: state.parkingContextUuid ?? undefined
            }
        })
    }

    const handlePolicyChange = (opt: Value) => {
        updateState({ parkingContextUuid: opt[0].uuid })

        if (!state.parkingTariff) return

        calculatePrice({
            variables: {
                order_id,
                selected_parking_tariff: state.parkingTariff,
                parking_context_uuid: opt[0].uuid ?? undefined
            }
        })
    }

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    if (error) {
        return <Error />
    }

    if (!selectedOrder || !data?.b2c_get_open_orders) return <div>{t('carpay.notFound.orderNotFound')}</div>

    const orderPriceResponse = getFragmentData(OrderPriceResponseFragment, priceData?.b2c_get_price)
    const queryparams = new URLSearchParams(location.search)

    const tariffs = parseTariffs(
        selectedOrder.lot.lot_tariffs,
        selectedOrder.order.start_date,
        selectedOrder.lot.sv_lot_type === SvLotType.TollRoad
    )

    const policies = selectedOrder.parking_contexts.map((policy) => ({
        id: policy.uuid,
        uuid: policy.uuid || undefined,
        label: t(`carpay.paymentInput.vehicleTypes.${policy?.vehicle_type?.toLowerCase()}`) ?? ''
    }))

    const hasDiscountTypes = selectedOrder.lot.discount_types.length > 0
    const hasNoScannedCodes = selectedOrder.scanned_codes.length === 0
    policies.unshift({ id: 'car', uuid: undefined, label: t('carpay.paymentInput.vehicleTypes.car') })

    const hasTariffSelected = tariffs.length === 0 || state.parkingTariff !== null
    const hasPolicySelected = policies.length === 0 || state.parkingContextUuid !== null
    const allowDiscount =
        hasDiscountTypes && hasNoScannedCodes && hasTariffSelected && hasPolicySelected && !loadingPrice

    const handleNextStep = (e: FormEvent<HTMLButtonElement>) => {
        e.preventDefault()
        history.push(`${carpayPaymentEmailRoute(order_id)}?${queryparams.toString()}`)
    }

    const selectedTariff = tariffs.find((tariff) => tariff.id === state.parkingTariff)
    const selectedPolicy = policies.find((policy) => policy.uuid === state.parkingContextUuid)

    const currency = selectedOrder.price.currency
    const parkingPrice = orderPriceResponse?.price.parking_price ?? 0
    const illegalParkingPrice = orderPriceResponse?.price.illegal_parking_price ?? 0
    const whitelistDiscount = Math.max(parkingPrice - illegalParkingPrice, 0)
    const voucherDiscount = orderPriceResponse?.price.applied_discount
    const priceToBePaid = orderPriceResponse?.price.discounted_parking_price ?? 0
    const priceValidUntil = orderPriceResponse?.price.price_valid_until

    // Updating cache to display the correct price_valid_until value after successful payment
    if (orderPriceResponse) {
        client.writeFragment({
            id: order_id,
            fragment: OrderResponseFragment,
            data: {
                ...selectedOrder,
                price: { ...selectedOrder.price, ...orderPriceResponse.price }
            }
        })
    }

    return (
        <Container>
            <TitleContainer>
                <Heading>{t('carpay.paymentInput.title')}</Heading>
                <Title>{t('carpay.paymentInput.subtitle')}</Title>
            </TitleContainer>

            <FormContainer onSubmit={() => null}>
                <SelectsContainer>
                    {policies.length > 0 ? (
                        <PolicyContextSelect
                            options={policies}
                            value={selectedPolicy ? ([selectedPolicy] as Value) : null}
                            onChange={handlePolicyChange}
                        />
                    ) : null}

                    <TariffSelect
                        options={tariffs}
                        value={selectedTariff ? ([selectedTariff] as Value) : null}
                        onChange={handleTariffChange}
                    />
                </SelectsContainer>
                <EmptyCarpayOrderCard
                    fullPrice={parkingPrice}
                    voucherDiscount={voucherDiscount}
                    whitelistDiscount={whitelistDiscount}
                    priceValidUntil={priceValidUntil}
                    price={priceToBePaid ?? 0}
                    isLast={true}
                    loading={loadingPrice}
                    currency={currency}
                />
                <Button
                    id="user-input-next-button"
                    disabled={
                        loadingPrice ||
                        (lotType === 'PREPAID' && !hasTariffSelected) ||
                        !hasPolicySelected ||
                        !priceData ||
                        priceToBePaid === 0
                    }
                    $kind={BUTTON_KIND.primary}
                    $size={BUTTON_SIZE.medium}
                    type="submit"
                    onClick={handleNextStep}
                >
                    {t('carpay.paymentInput.next')}
                </Button>

                <Voucher disabled={!allowDiscount} orderData={selectedOrder} />
            </FormContainer>
        </Container>
    )
}

export default CarpayPaymentInput
